import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";
import CustomConfirm from 'ra-custom-confirm';
import Check from '@material-ui/icons/Check';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
  },
  container: {
    width: "100%",
    maxWidth: "600px",
  },
  pageTitle: {
    marginBottom: "1.5em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  card: {
    padding: "2em",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.5em",
    marginBottom: "1.5em",
  },
  headerIcon: {
    fontSize: "2rem",
    color: theme.palette.primary.main,
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  button: {
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    },
  },
  warningBox: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ffb74d",
    borderRadius: "8px",
    padding: "1em",
    marginBottom: "1.5em",
  },
  warningText: {
    color: "#e65100",
    fontSize: "0.9rem",
  },
  successBox: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #81c784",
    borderRadius: "8px",
    padding: "1em",
    marginTop: "1.5em",
  },
  successText: {
    color: "#2e7d32",
    fontSize: "0.95rem",
  },
}));

interface FormValues {
  amount?: string;
}

const { Form } = withTypes<FormValues>();

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  helperText,
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={(touched && error) || helperText}
    {...inputProps}
    {...props}
    fullWidth
    variant="outlined"
  />
);

const LoadMoneyPpsSandboxAccount = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<any>(null);

  const handleLoadClick = (values: FormValues) => {
    const amount = parseFloat(values.amount || "0");
    if (amount <= 0) {
      notify("Please enter a valid amount greater than 0", "error", "", undefined, 5000);
      return;
    }

    setPendingAmount(values.amount || "0");
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingAmount) return;

    setLoading(true);
    setConfirmOpen(false);
    setLastResult(null);

    const headers = getConfigPost();
    const data = {
      amount: parseFloat(pendingAmount),
    };

    axios
      .post(ENDPOINTS.LOAD_MONEY_PPS_SANDBOX_ACCOUNT, data, { headers })
      .then((res: any) => {
        setLoading(false);
        const responseData = res?.data;
        if (responseData?.status) {
          setLastResult(responseData);
          notify(
            responseData?.message || "Amount loaded successfully!",
            "info",
            "",
            undefined,
            10000
          );
        } else {
          notify(
            responseData?.message || "Failed to load amount",
            "error",
            "",
            undefined,
            5000
          );
        }
        setPendingAmount(null);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          error?.response?.data?.message || error?.message || "Failed to load amount",
          "error",
          "",
          undefined,
          5000
        );
        setPendingAmount(null);
      });
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingAmount(null);
  };

  const validate = (values: FormValues) => {
    const errors: any = {};
    if (!values.amount) {
      errors.amount = "Amount is required";
    } else if (parseFloat(values.amount) <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    return errors;
  };

  return (
    <>
      <Form
        onSubmit={handleLoadClick}
        validate={validate}
        render={({ handleSubmit, values, form }) => (
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.main}>
              <div className={classes.container}>
                <Typography variant="h4" className={classes.pageTitle}>
                  Load Money to PPS Sandbox Account
                </Typography>

                <Paper className={classes.card} elevation={2}>
                  <div className={classes.cardHeader}>
                    <AccountBalanceWalletIcon className={classes.headerIcon} />
                    <Typography variant="h6">Load Amount</Typography>
                  </div>

                  <Box className={classes.warningBox}>
                    <Typography className={classes.warningText}>
                      This will load money to the PPS Master Referral eWallet account from the PPS Load Money source account.
                      This is typically used for sandbox/testing environments.
                    </Typography>
                  </Box>

                  <Field
                    name="amount"
                    component={renderInput}
                    label="Amount (£) *"
                    type="number"
                    className={classes.formField}
                    disabled={loading}
                    inputProps={{ min: 0, step: "0.01" }}
                    helperText="Enter the amount to load in GBP"
                    InputLabelProps={{ shrink: true }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading || !values.amount || parseFloat(values.amount || "0") <= 0}
                    className={classes.button}
                    fullWidth
                  >
                    {loading && (
                      <CircularProgress size={18} thickness={2} style={{ marginRight: "10px", color: "white" }} />
                    )}
                    Load Money
                  </Button>

                  {lastResult && lastResult.status && (
                    <Box className={classes.successBox}>
                      <Typography className={classes.successText}>
                        <strong>Success!</strong> {lastResult.message}
                        {lastResult.data?.txn_ref && (
                          <><br />Transaction Ref: <strong>{lastResult.data.txn_ref}</strong></>
                        )}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                <Notification />
              </div>
            </div>

            <CustomConfirm
              isOpen={confirmOpen}
              title="Confirm Load Money"
              user={{ amount: pendingAmount }}
              content={ConfirmContent}
              confirm="Confirm"
              confirmColor="primary"
              ConfirmIcon={Check}
              cancel="Cancel"
              CancelIcon={ErrorOutline}
              onConfirm={handleConfirm}
              onClose={handleCancel}
            />
          </form>
        )}
      />
    </>
  );
};

const ConfirmContent = (props: any) => {
  const data = props.user;
  if (!data) return null;

  return (
    <div style={{ padding: "1.5em" }}>
      <Box
        style={{
          padding: "1.5em",
          backgroundColor: "#f5f7fa",
          borderRadius: "8px",
          marginBottom: "1em",
        }}
      >
        <Typography variant="body1" gutterBottom style={{ marginBottom: "0.8em" }}>
          <strong>Amount to Load:</strong>{" "}
          <span style={{ color: "#2e7d32", fontSize: "1.2rem", fontWeight: 600 }}>
            £{parseFloat(data.amount).toFixed(2)}
          </span>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          This will transfer funds to the PPS Master Referral eWallet account.
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ textAlign: "center", fontStyle: "italic" }}
      >
        Are you sure you want to proceed? This action cannot be undone.
      </Typography>
    </div>
  );
};

export default LoadMoneyPpsSandboxAccount;
